import React, { useEffect, useState } from 'react';
import { fetchCampaignOptions, fetchRetryRules } from '../../../services/callcenterV2/campaignsService';
import { saoPauloInputToUtcIso, utcIsoToSaoPauloInput } from '../../../utils/datetime';
import {
  DEFAULT_RETRY_RULES,
  DELAY_PRESETS,
  DELAY_UNITS,
  RETRY_DISPOSITION_LABELS,
  delayPartsToSeconds,
  formatHumanDelay,
  rulesToUi,
  uiRulesToPayload,
} from './retryDelay';

const DEFAULT_FORM = {
  name: '',
  description: '',
  dialMode: 'power',
  queueName: '',
  trunkName: '',
  callerId: '',
  powerRatio: 1,
  maxConcurrent: 10,
  cpsLimit: 2,
  dialTimeoutSeconds: 30,
  queueTimeoutSeconds: 60,
  maxAttemptsPerLead: 6,
  maxAttemptsPerPhone: 3,
  respectUfWindows: true,
  scheduledStartAt: '',
  scheduledEndAt: '',
};

const TABS = [
  { key: 'general', label: 'Geral' },
  { key: 'dialing', label: 'Discagem' },
  { key: 'schedule', label: 'Agendamento' },
];

export function campaignToForm(campaign) {
  if (!campaign) return { ...DEFAULT_FORM };
  const toLocalInput = (iso) => utcIsoToSaoPauloInput(iso);
  return {
    name: campaign.name || '',
    description: campaign.description || '',
    dialMode: campaign.dialMode || 'power',
    queueName: campaign.queueName || '',
    trunkName: campaign.trunkName || '',
    callerId: campaign.callerId || '',
    powerRatio: campaign.powerRatio ?? 1,
    maxConcurrent: campaign.maxConcurrent ?? 10,
    cpsLimit: campaign.cpsLimit ?? 2,
    dialTimeoutSeconds: campaign.dialTimeoutSeconds ?? 30,
    queueTimeoutSeconds: campaign.queueTimeoutSeconds ?? 60,
    maxAttemptsPerLead: campaign.maxAttemptsPerLead ?? 6,
    maxAttemptsPerPhone: campaign.maxAttemptsPerPhone ?? 3,
    respectUfWindows: campaign.respectUfWindows !== false,
    scheduledStartAt: toLocalInput(campaign.scheduledStartAt),
    scheduledEndAt: toLocalInput(campaign.scheduledEndAt),
  };
}

export function formToPayload(form, { isUpdate = false } = {}) {
  const payload = {
    name: form.name.trim(),
    description: form.description.trim() || undefined,
    dialMode: form.dialMode,
    queueName: form.queueName.trim(),
    trunkName: form.trunkName.trim(),
    powerRatio: Number(form.powerRatio),
    maxConcurrent: Number(form.maxConcurrent),
    cpsLimit: Number(form.cpsLimit),
    dialTimeoutSeconds: Number(form.dialTimeoutSeconds),
    queueTimeoutSeconds: Number(form.queueTimeoutSeconds),
    maxAttemptsPerLead: Number(form.maxAttemptsPerLead),
    maxAttemptsPerPhone: Number(form.maxAttemptsPerPhone),
    respectUfWindows: Boolean(form.respectUfWindows),
  };

  const callerId = form.callerId.trim();
  if (callerId) payload.callerId = callerId;
  else if (isUpdate) payload.callerId = '';

  const scheduledStartAt = saoPauloInputToUtcIso(form.scheduledStartAt);
  const scheduledEndAt = saoPauloInputToUtcIso(form.scheduledEndAt);
  if (scheduledStartAt) payload.scheduledStartAt = scheduledStartAt;
  if (scheduledEndAt) payload.scheduledEndAt = scheduledEndAt;

  return payload;
}

function withCurrent(list, current) {
  const values = Array.isArray(list) ? list : [];
  if (current && !values.includes(current)) return [current, ...values];
  return values;
}

function TargetSelect({ value, onChange, disabled, options, loading, failed, emptyLabel, placeholder }) {
  if (failed) {
    return <input value={value} onChange={onChange} disabled={disabled} />;
  }
  const choices = withCurrent(options, value);
  const emptyOptionLabel = loading ? 'Carregando...' : choices.length === 0 ? emptyLabel : placeholder;
  return (
    <select value={value} onChange={onChange} disabled={disabled || loading}>
      <option value="">{emptyOptionLabel}</option>
      {choices.map((name) => (
        <option key={name} value={name}>{name}</option>
      ))}
    </select>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="ccv2-form-group">
      <label>{label}</label>
      {children}
      {hint && <p className="ccv2-hint">{hint}</p>}
    </div>
  );
}

function RetryRulesEditor({ rules, onChange }) {
  if (rules === undefined) {
    return <p className="ccv2-hint">Carregando regras de retentativa...</p>;
  }
  if (!rules) {
    return (
      <p className="ccv2-hint">
        Não foi possível carregar as regras atuais. A campanha será salva sem alterar o atraso.
      </p>
    );
  }

  const update = (index, patch) => {
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  return (
    <div className="ccv2-retry-editor">
      <div className="ccv2-card-title">
        <div>
          <h3>Quando tentar de novo</h3>
          <p>
            Se o cliente não atender, o discador espera 2 horas antes da próxima tentativa neste telefone.
          </p>
        </div>
      </div>
      <div className="ccv2-config-table-wrapper">
        <table className="ccv2-config-table">
          <thead>
            <tr>
              <th>Se a chamada</th>
              <th>Esperar</th>
              <th>Máx. tentativas</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule, index) => {
              const seconds = delayPartsToSeconds(rule.delayValue, rule.delayUnit);
              return (
                <tr key={rule.disposition}>
                  <td>{RETRY_DISPOSITION_LABELS[rule.disposition] || rule.disposition}</td>
                  <td>
                    <div className="ccv2-retry-delay">
                      <input
                        className="ccv2-penalty-input"
                        type="number"
                        min="1"
                        step="1"
                        value={rule.delayValue}
                        onChange={(event) => update(index, { delayValue: event.target.value })}
                      />
                      <select
                        className="ccv2-retry-unit-select"
                        value={rule.delayUnit}
                        aria-label="Unidade de espera"
                        onChange={(event) => update(index, { delayUnit: event.target.value })}
                      >
                        {DELAY_UNITS.map((unit) => (
                          <option key={unit.value} value={unit.value}>{unit.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="ccv2-retry-presets">
                      {DELAY_PRESETS.map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          className="ccv2-btn ccv2-btn-outline ccv2-btn-sm"
                          onClick={() => update(index, { delayValue: preset.value, delayUnit: preset.unit })}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <p className="ccv2-hint">Equivale a {formatHumanDelay(seconds)} ({seconds} s).</p>
                  </td>
                  <td>
                    <input
                      className="ccv2-penalty-input"
                      type="number"
                      min="1"
                      max="20"
                      value={rule.maxAttempts}
                      onChange={(event) => update(index, { maxAttempts: event.target.value })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CampaignForm({ campaign, saving, onCancel, onSubmit }) {
  const [form, setForm] = useState(() => campaignToForm(campaign));
  const [retryRules, setRetryRules] = useState(() => (
    campaign?.idDialerCampaign ? undefined : rulesToUi(DEFAULT_RETRY_RULES)
  ));
  const [tab, setTab] = useState('general');
  const [options, setOptions] = useState(null);
  const [optionsFailed, setOptionsFailed] = useState(false);
  const isUpdate = Boolean(campaign);
  const structuralLocked = isUpdate && ['running', 'draining'].includes(campaign.status);

  useEffect(() => {
    let active = true;
    fetchCampaignOptions()
      .then((data) => {
        if (active) setOptions(data);
      })
      .catch(() => {
        if (active) setOptionsFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!campaign?.idDialerCampaign) {
      setRetryRules(rulesToUi(DEFAULT_RETRY_RULES));
      return undefined;
    }
    let active = true;
    fetchRetryRules(campaign.idDialerCampaign)
      .then((data) => {
        if (active) setRetryRules(rulesToUi(data));
      })
      .catch(() => {
        if (active) setRetryRules(null);
      });
    return () => {
      active = false;
    };
  }, [campaign]);

  const set = (field) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submit = () => onSubmit(
    formToPayload(form, { isUpdate }),
    retryRules ? uiRulesToPayload(retryRules) : null,
  );
  const canSubmit = form.name.trim() && form.queueName.trim() && form.trunkName.trim() && !saving && retryRules !== undefined;

  return (
    <div className="ccv2-modal-overlay" onClick={onCancel}>
      <div className="ccv2-modal ccv2-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="ccv2-modal-header">
          <h2>{isUpdate ? `Editar campanha: ${campaign.name}` : 'Nova campanha'}</h2>
          <button type="button" className="ccv2-modal-close" onClick={onCancel}>×</button>
        </div>

        <div className="ccv2-modal-body">
          <div className="ccv2-tabs">
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`ccv2-tab ${tab === item.key ? 'active' : ''}`}
                onClick={() => setTab(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {tab === 'general' && (
            <>
              <Field label="Nome *">
                <input value={form.name} onChange={set('name')} maxLength={120} placeholder="ex.: Cobrança setembro" />
              </Field>
              <Field label="Descrição">
                <textarea rows={2} value={form.description} onChange={set('description')} maxLength={2000} />
              </Field>
              <div className="ccv2-form-row">
                <Field
                  label="Fila de entrega *"
                  hint="Use uma fila dedicada: misturar com o inbound distorce SLA e abandono do Monitoramento."
                >
                  <TargetSelect
                    value={form.queueName}
                    onChange={set('queueName')}
                    disabled={structuralLocked}
                    options={options?.queues}
                    loading={!options && !optionsFailed}
                    failed={optionsFailed}
                    emptyLabel="Nenhuma fila cadastrada para o tenant"
                    placeholder="Selecione a fila"
                  />
                </Field>
                <Field label="Tronco de saída *" hint="Precisa pertencer ao tenant (accountcode do endpoint).">
                  <TargetSelect
                    value={form.trunkName}
                    onChange={set('trunkName')}
                    disabled={structuralLocked}
                    options={options?.trunks}
                    loading={!options && !optionsFailed}
                    failed={optionsFailed}
                    emptyLabel="Nenhum tronco cadastrado para o tenant"
                    placeholder="Selecione o tronco"
                  />
                </Field>
              </div>
              {optionsFailed && (
                <div className="ccv2-p5-error">
                  Não foi possível carregar filas e troncos do tenant; preencha manualmente.
                </div>
              )}
              <Field label="Caller ID" hint="Vazio usa o padrão do tronco. Se preenchido, precisa ser um DID do tenant.">
                <input
                  value={form.callerId}
                  onChange={set('callerId')}
                  placeholder="ex.: 1130000000"
                  list="ccv2-campaign-callerid-options"
                />
                {(options?.callerIds || []).length > 0 && (
                  <datalist id="ccv2-campaign-callerid-options">
                    {options.callerIds.map((did) => (
                      <option key={did} value={did} />
                    ))}
                  </datalist>
                )}
              </Field>
              {structuralLocked && (
                <div className="ccv2-p5-error">
                  Fila, tronco e modo de discagem não podem mudar com a campanha ativa.
                </div>
              )}
            </>
          )}

          {tab === 'dialing' && (
            <>
              <div className="ccv2-form-row">
                <Field label="Modo de discagem">
                  <select value={form.dialMode} onChange={set('dialMode')} disabled={structuralLocked}>
                    <option value="power">Power (proporcional aos agentes livres)</option>
                    <option value="preview">Preview (agente confirma antes)</option>
                  </select>
                </Field>
                <Field label="Power ratio" hint="Chamadas por agente disponível (1 a 10).">
                  <input type="number" step="0.1" min="1" max="10" value={form.powerRatio} onChange={set('powerRatio')} />
                </Field>
              </div>
              <div className="ccv2-form-row">
                <Field label="Máximo simultâneo" hint="Teto de canais da campanha (1 a 500).">
                  <input type="number" min="1" max="500" value={form.maxConcurrent} onChange={set('maxConcurrent')} />
                </Field>
                <Field label="Limite de CPS" hint="Chamadas por segundo no tronco (0.1 a 50).">
                  <input type="number" step="0.1" min="0.1" max="50" value={form.cpsLimit} onChange={set('cpsLimit')} />
                </Field>
              </div>
              <div className="ccv2-form-row">
                <Field label="Timeout de discagem (s)" hint="Tempo máximo tocando antes de desistir.">
                  <input type="number" min="5" max="120" value={form.dialTimeoutSeconds} onChange={set('dialTimeoutSeconds')} />
                </Field>
                <Field label="Timeout na fila (s)" hint="Estouro conta como abandono na compliance.">
                  <input type="number" min="5" max="600" value={form.queueTimeoutSeconds} onChange={set('queueTimeoutSeconds')} />
                </Field>
              </div>
              <div className="ccv2-form-row">
                <Field label="Tentativas por lead">
                  <input type="number" min="1" max="50" value={form.maxAttemptsPerLead} onChange={set('maxAttemptsPerLead')} />
                </Field>
                <Field label="Tentativas por telefone">
                  <input type="number" min="1" max="20" value={form.maxAttemptsPerPhone} onChange={set('maxAttemptsPerPhone')} />
                </Field>
              </div>
              <RetryRulesEditor rules={retryRules} onChange={setRetryRules} />
            </>
          )}

          {tab === 'schedule' && (
            <>
              <div className="ccv2-form-row">
                <Field label="Início programado">
                  <input type="datetime-local" value={form.scheduledStartAt} onChange={set('scheduledStartAt')} />
                </Field>
                <Field label="Fim programado" hint="Ao vencer, a campanha entra em drenagem automaticamente.">
                  <input type="datetime-local" value={form.scheduledEndAt} onChange={set('scheduledEndAt')} />
                </Field>
              </div>
              <Field label="Janelas de horário por UF" hint="Respeita a janela legal do DDD do telefone (recomendado ANATEL).">
                <label className="ccv2-checkbox-item">
                  <input type="checkbox" checked={form.respectUfWindows} onChange={set('respectUfWindows')} />
                  <span>Respeitar janelas por UF</span>
                </label>
              </Field>
            </>
          )}
        </div>

        <div className="ccv2-modal-footer">
          <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={submit} disabled={!canSubmit}>
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CampaignForm;
